import React, { useEffect, useRef, useState } from 'react';
import { Trash2, Edit2, Download, Syringe, Loader2, X, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Immunization } from '../../types/immunization';
import { API_ENDPOINTS } from '../../config/api';

interface ImmunizationsListProps {
    petId: string;
    refreshTrigger?: number;
    onEdit: (immunization: Immunization) => void;
}

export const ImmunizationsList: React.FC<ImmunizationsListProps> = ({ petId, refreshTrigger = 0, onEdit }) => {
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const [immunizations, setImmunizations] = useState<Immunization[]>([]);
    const [loading, setLoading] = useState(true);
    const [downloadingRecords, setDownloadingRecords] = useState(false);
    const [downloadingCertificate, setDownloadingCertificate] = useState(false);
    
    // Delete confirmation modal state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [immunizationToDelete, setImmunizationToDelete] = useState<Immunization | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [focusedId, setFocusedId] = useState<string | null>(null);
    const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});

    const fetchImmunizations = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(API_ENDPOINTS.IMMUNIZATIONS.BY_PET(petId), {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setImmunizations(data);
            }
        } catch (error) {
            console.error('Error fetching immunizations:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchImmunizations();
    }, [petId, refreshTrigger]);

    // Deep-link focus support: /immunizations?focus=Dog%20Flu
    useEffect(() => {
        if (loading) return;
        const focus = (searchParams.get('focus') || '').trim().toLowerCase();
        if (!focus || immunizations.length === 0) return;

        const match = immunizations.find((imm) => (imm.name || '').toLowerCase().includes(focus));
        if (!match) return;

        setFocusedId(match.id);
        const el = rowRefs.current[match.id];
        if (el) {
            el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }

        const t = window.setTimeout(() => setFocusedId(null), 3500);
        return () => window.clearTimeout(t);
    }, [loading, immunizations, searchParams]);

    // Handle ESC key to close delete modal
    useEffect(() => {
        const handleEscKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && showDeleteModal) {
                setShowDeleteModal(false);
                setImmunizationToDelete(null);
            }
        };
        document.addEventListener('keydown', handleEscKey);
        return () => document.removeEventListener('keydown', handleEscKey);
    }, [showDeleteModal]);

    const handleDelete = (immunization: Immunization) => {
        setImmunizationToDelete(immunization);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!immunizationToDelete) return;

        setDeleting(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(API_ENDPOINTS.IMMUNIZATIONS.DELETE(immunizationToDelete.id), {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                fetchImmunizations();
                setShowDeleteModal(false);
                setImmunizationToDelete(null);
            }
        } catch (error) {
            console.error('Error deleting immunization:', error);
        } finally {
            setDeleting(false);
        }
    };

    const handleDownloadRecords = async () => {
        setDownloadingRecords(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_ENDPOINTS.IMMUNIZATIONS.BY_PET(petId)}/export`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const contentDisposition = response.headers.get('Content-Disposition');
                const filenameMatch = contentDisposition?.match(/filename=(.+)/);
                const filename = filenameMatch ? filenameMatch[1] : 'vaccination_record.pdf';
                
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            } else {
                console.error('Failed to download records');
            }
        } catch (error) {
            console.error('Error downloading records:', error);
        } finally {
            setDownloadingRecords(false);
        }
    };

    const handleDownloadRabiesCertificate = async () => {
        setDownloadingCertificate(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_ENDPOINTS.IMMUNIZATIONS.BY_PET(petId)}/rabies-certificate`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const html = await response.text();
                // Open in new window for printing
                const printWindow = window.open('', '_blank');
                if (printWindow) {
                    printWindow.document.write(html);
                    printWindow.document.close();
                }
            } else if (response.status === 404) {
                alert('No rabies vaccination records found for this pet.');
            } else {
                console.error('Failed to get rabies certificate');
            }
        } catch (error) {
            console.error('Error getting rabies certificate:', error);
        } finally {
            setDownloadingCertificate(false);
        }
    };

    const formatDate = (dateString: string | undefined) => {
        if (!dateString) return '-';
        // Use UTC timezone to avoid date shift due to local timezone offset
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC'
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 px-6">
            {/* Header */}
            <div className="flex items-center justify-between">
            </div>
            
            <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-gray-50 dark:bg-dark-bg-tertiary border-b border-gray-200 dark:border-dark-border">
                            <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Immunization</th>
                            <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Administered</th>
                            <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Due Date</th>
                            <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Note</th>
                            <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Encounter</th>
                            <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-center">Edit</th>
                            <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-center">Delete</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-dark-border">
                        {immunizations.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="px-4 py-12 text-center text-gray-500 dark:text-gray-400">
                                    <Syringe className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                                    <p className="text-lg font-medium text-gray-900 dark:text-dark-text-primary">No immunizations found</p>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">Add a new immunization record to get started.</p>
                                </td>
                            </tr>
                        ) : (
                            immunizations.map((imm) => (
                                <tr
                                    key={imm.id}
                                    ref={(el) => { rowRefs.current[imm.id] = el; }}
                                    className={`hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors group ${
                                        focusedId === imm.id ? 'bg-yellow-50 dark:bg-yellow-900/20' : ''
                                    }`}
                                >
                                    <td className="px-4 py-3">
                                        <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">{imm.name}</div>
                                        {(imm.site || imm.manufacturer || imm.lot_number) && (
                                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                {[imm.site, imm.manufacturer, imm.lot_number].filter(Boolean).join(' · ')}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-dark-text-primary whitespace-nowrap">{formatDate(imm.administered_date)}</td>
                                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                        {imm.due_date 
                                            ? new Date(imm.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
                                            : '-'
                                        }
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 max-w-[200px] truncate" title={imm.note}>
                                        {imm.note || '-'}
                                    </td>
                                    <td className="px-4 py-3 text-sm">
                                        {imm.encounter_id ? (
                                            <a
                                                href={`/pet/${petId}/encounter/${imm.encounter_id}/intake`}
                                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 dark:bg-blue-900/30 dark:text-blue-400 border border-blue-200 dark:border-blue-800 rounded hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors whitespace-nowrap"
                                            >
                                                {imm.encounter_date ? new Date(imm.encounter_date).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '-') : 'View'}
                                            </a>
                                        ) : (
                                            <span className="text-gray-400">—</span>
                                        )}
                                    </td>
                                    <td className="px-2 py-3 text-center">
                                        <button
                                            onClick={() => onEdit(imm)}
                                            className="text-gray-400 hover:text-blue-600 transition-colors p-1.5 rounded hover:bg-blue-50 dark:hover:bg-blue-900/30"
                                            title="Edit"
                                        >
                                            <Edit2 className="w-4 h-4" />
                                        </button>
                                    </td>
                                    <td className="px-2 py-3 text-center">
                                        <button
                                            onClick={() => handleDelete(imm)}
                                            className="text-gray-400 hover:text-red-600 transition-colors p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/30"
                                            title="Delete"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <div className="flex gap-4 pt-4">
                <button 
                    onClick={handleDownloadRecords}
                    disabled={downloadingRecords || immunizations.length === 0}
                    className={`flex items-center gap-2 px-4 py-2 border rounded-none shadow-sm text-sm font-medium transition-colors ${
                        downloadingRecords || immunizations.length === 0
                            ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                    title={immunizations.length === 0 ? 'No vaccination records to download' : 'Download vaccination records as PDF'}
                >
                    {downloadingRecords ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <Download className="w-4 h-4" />
                    )}
                    Download Vaccination Record (PDF)
                </button>
                <button 
                    onClick={handleDownloadRabiesCertificate}
                    disabled={downloadingCertificate || immunizations.length === 0}
                    className={`flex items-center gap-2 px-4 py-2 border rounded-none shadow-sm text-sm font-medium transition-colors ${
                        downloadingCertificate || immunizations.length === 0
                            ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                    title={immunizations.length === 0 ? 'No immunization records' : 'Open printable rabies certificate'}
                >
                    {downloadingCertificate ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <Download className="w-4 h-4" />
                    )}
                    Download Rabies Certificates
                </button>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && immunizationToDelete && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-xl w-full max-w-md mx-4">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-dark-border">
                            <div className="flex items-center gap-3">
                                <div className="flex items-center justify-center w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full">
                                    <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">Delete Immunization</h3>
                            </div>
                            <button
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setImmunizationToDelete(null);
                                }}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6">
                            <p className="text-gray-600 dark:text-gray-400 mb-4">
                                Are you sure you want to delete this immunization record? This action cannot be undone.
                            </p>
                            <div className="bg-gray-50 dark:bg-dark-bg-tertiary rounded-lg p-4 mb-6">
                                <p className="font-medium text-gray-900 dark:text-dark-text-primary">{immunizationToDelete.name}</p>
                                {immunizationToDelete.administered_date && (
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                        Administered: {new Date(immunizationToDelete.administered_date).toLocaleDateString('en-US', { 
                                            year: 'numeric', 
                                            month: 'short', 
                                            day: 'numeric',
                                            timeZone: 'UTC'
                                        })}
                                    </p>
                                )}
                            </div>

                            <div className="flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowDeleteModal(false);
                                        setImmunizationToDelete(null);
                                    }}
                                    disabled={deleting}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-dark-bg-tertiary border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={confirmDelete}
                                    disabled={deleting}
                                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                                >
                                    {deleting ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Deleting...
                                        </>
                                    ) : (
                                        'Delete Immunization'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
